import React, { type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react';
import c from 'classnames';

import { Button, Icon, isExternalLink } from '@uncinc/uncinc-react-kitchen-sink';

interface BaseProps {
  type?: 'primary' | 'secondary' | 'textual' | 'tertiary';
  variant?: 'icon-only';
  iconName?: string;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  inverted?: boolean;
}

interface TypeAnchorProps extends BaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type'> {
  href: string;
}

interface TypeButtonProps extends BaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

export type ButtonWrapperProps = TypeAnchorProps | TypeButtonProps;

const ButtonWrapper: React.FC<ButtonWrapperProps> = (props) => {
  const {
    type = 'primary',
    variant,
    iconName = 'arrow_forward',
    iconPosition = 'right',
    loading,
    disabled,
    className,
    children,
    onClick,
    inverted = false,
    ...restProps
  } = props;
  let icon = iconName;

  const buttonClasses = c(className, 'icon-arrow-right icon-before icon-after moving-arrow', `Button--${type}`, {
    [`Button--variant--${String(variant)}`]: variant !== undefined,
    [`Button--icon-position--${iconPosition}`]: icon,
    'Button--disabled': loading || disabled,
  });

  if ('href' in props && !iconName) {
    const { href } = props;
    icon = !isExternalLink(href) ? 'arrow_forward' : 'arrow_outward';
  }

  return (
    <Button
      {...restProps}
      componentIsWrapped
      primary={false}
      className={buttonClasses}
      aria-disabled={disabled}
      inverted={inverted}
      onClick={(
        event: React.MouseEvent<HTMLAnchorElement> & React.MouseEvent<HTMLButtonElement>,
      ) => {
        if (loading || disabled) {
          return;
        }

        if (onClick) {
          onClick(event);
        }
      }}
    >
      {variant !== 'icon-only' && children}
    </Button>
  );
};


export default ButtonWrapper;
